import React, { memo, useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react';
import debounce from 'lodash/debounce';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { useDocChangeContext } from '@confluence/editor-features/entry-points/useDocChangeContext.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';
import { type FullPageEditorPresetAPI } from '@confluence/full-page-editor-store';

import { ReadTimeQuery } from './hooks/useReadTime.tsx';
import type {
	useReadTimeQuery$data,
	useReadTimeQuery$variables,
} from './hooks/__generated__/useReadTimeQuery.graphql.ts';
import { ReadTimePopupWrapper } from './ReadTimePopupWrapper.tsx';
import {
	getAdf,
	getAdfString,
	getContentFeatureExtractedEventObject,
	readTimeFromFeatures,
} from './readTimeUtils.ts';
import { extractAllContentFeatures, type ContentFeatures } from './featuresExtractor.ts';

type EditorReadTimeProps = {
	contentId: string;
	editorApi?: FullPageEditorPresetAPI | null;
	showWordCount?: boolean;
	isDetailsPanel?: boolean;
	isAbbreviatedReadTime?: boolean;
};

/**
 * Read Time component with support for automatically recalculating and updating read time after a document change in the editor.
 */
export const EditorReadTime: FC<EditorReadTimeProps> = memo(
	({
		contentId,
		editorApi,
		showWordCount,
		isDetailsPanel,
		isAbbreviatedReadTime = true,
	}: EditorReadTimeProps) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { subscribeDocChange, unsubscribeDocChange } = useDocChangeContext();

		const readTimeQueryOptions = {
			variables: {
				contentId,
				status: ['current', 'draft'],
			},
		};

		const { data, loading, error } = useQueryNoFurtherUpdateInterop<
			useReadTimeQuery$data,
			useReadTimeQuery$variables
		>(ReadTimeQuery, readTimeQueryOptions);

		const adf = useMemo(() => getAdf(data ?? undefined), [data]);
		const adfString = useMemo(() => getAdfString(data ?? undefined), [data]);

		// Read time + word count derived from the query ADF in a SINGLE pass (the
		// query data carries the raw ADF string, so its exact length is passed
		// through). Memoized so it only walks when the query ADF actually changes.
		// Features are NOT derived here — they are owned by the live editor
		// document (see below).
		const queryReadTime = useMemo((): {
			readTime: number;
			wordCount: number | null;
			isReadTimeCapped: boolean;
		} => {
			if (!adf) {
				return { readTime: 0, wordCount: 0, isReadTimeCapped: false };
			}

			const { featuresSkipExpand, nodeCount } = extractAllContentFeatures(adf, adfString?.length);
			const { readTime, isReadTimeCapped } = readTimeFromFeatures(featuresSkipExpand, nodeCount);
			return {
				readTime,
				wordCount: isReadTimeCapped ? null : featuresSkipExpand.wordCount,
				isReadTimeCapped,
			};
		}, [adf, adfString]);

		// Initialize with preloaded query data for SSR.
		const [readTime, setReadTime] = useState<number | null>(queryReadTime.readTime);
		const [wordCount, setWordCount] = useState<number | null>(queryReadTime.wordCount);
		const [isReadTimeCapped, setIsReadTimeCapped] = useState(queryReadTime.isReadTimeCapped);
		const [features, setFeatures] = useState<ContentFeatures | null>(null);
		const [featuresSkipExpand, setFeaturesSkipExpand] = useState<ContentFeatures | null>(null);
		const lastAdfStringRef = useRef(adfString);

		useEffect(() => {
			if (!createAnalyticsEvent || !adf || loading || error || !contentId) {
				return;
			}
			const contentType = data?.content?.nodes?.[0]?.type ?? '';
			createAnalyticsEvent(
				getContentFeatureExtractedEventObject({ contentType, contentId, adf, isInEditor: true }),
			).fire();
		}, [contentId, loading, adf, data?.content?.nodes, error, createAnalyticsEvent]);

		useEffect(() => {
			// Recompute read time from the query ADF only when it actually changed.
			// Features stay owned by the live editor document.
			if (adf && !loading && !error && adfString !== lastAdfStringRef.current) {
				lastAdfStringRef.current = adfString;
				setReadTime(queryReadTime.readTime);
				setWordCount(queryReadTime.wordCount);
				setIsReadTimeCapped(queryReadTime.isReadTimeCapped);
			}
		}, [adf, adfString, loading, error, queryReadTime]);

		const setReadTimeFromEditorState = useCallback(async () => {
			if (editorApi) {
				editorApi.core?.actions.requestDocument((doc) => {
					if (!doc) {
						setReadTime(0);
						setWordCount(0);
						setIsReadTimeCapped(false);
					} else {
						// Single pass over the live editor doc (no serialized string, so no
						// rawLength). Node count, measured during the walk, drives the capped
						// "{floor}+ min read" lower bound.
						const {
							features: allFeatures,
							featuresSkipExpand: allFeaturesSkipExpand,
							nodeCount,
						} = extractAllContentFeatures(doc);
						const { readTime: editorReadTime, isReadTimeCapped: editorCapped } =
							readTimeFromFeatures(allFeaturesSkipExpand, nodeCount);
						setReadTime(editorReadTime);
						setIsReadTimeCapped(editorCapped);
						if (editorCapped) {
							// Walk stopped early — counts are partial, treat as unavailable.
							setWordCount(null);
							setFeatures(null);
							setFeaturesSkipExpand(null);
						} else {
							setWordCount(allFeaturesSkipExpand.wordCount);
							setFeatures(allFeatures);
							setFeaturesSkipExpand(allFeaturesSkipExpand);
						}
					}
				});
			}
		}, [editorApi]);

		useEffect(() => {
			// Sometimes the ADF from the query is outdated if the byline is re-rendered quickly after a document change
			// So recalculate read time from the editor ADF when the editor is ready
			void setReadTimeFromEditorState();
		}, [setReadTimeFromEditorState]);

		const debouncedCalculateReadTime = useMemo(
			() => debounce(setReadTimeFromEditorState, 5000),
			[setReadTimeFromEditorState],
		);

		useEffect(() => {
			subscribeDocChange(debouncedCalculateReadTime);
			return () => unsubscribeDocChange(debouncedCalculateReadTime);
		}, [debouncedCalculateReadTime, subscribeDocChange, unsubscribeDocChange]);

		if (error) {
			return <ErrorDisplay error={error} />;
		}
		if (readTime || (showWordCount && wordCount != null)) {
			return (
				<ReadTimePopupWrapper
					contentId={contentId}
					isAbbreviatedReadTime={isAbbreviatedReadTime}
					isDetailsPanel={isDetailsPanel}
					showWordCount={showWordCount}
					features={features}
					featuresSkipExpand={featuresSkipExpand}
					wordCount={wordCount}
					readTime={readTime}
					source="editorReadTime"
					isReadTimeCapped={isReadTimeCapped}
				/>
			);
		}
	},
);
